import React from 'react';
import { Link, useLocation } from 'react-router-dom';
import { 
  QuestionMarkCircleIcon, 
  ExclamationTriangleIcon, 
  ChatBubbleLeftRightIcon,
  DocumentTextIcon,
  ShieldCheckIcon,
  InformationCircleIcon,
} from '@heroicons/react/24/outline';
import { useAppTheme } from '@/context/ThemeContext';
import { useAuthV2 } from '@/context/auth-v2/AuthContext';
// Layout is now handled in App.tsx routes


const HelpCenter: React.FC = () => {
  const { themeConfig } = useAppTheme();
  const location = useLocation();
  
  // Determine if we're in charity context based on the URL or user role
  const isCharityContext = () => {
    // Check URL path first
    if (location.pathname.includes('/charity/')) {
      return true;
    }
    // Then check user role from AuthContext
    const { user } = useAuthV2();
    return user?.role === 'charity';
  };
  
  // Determine the back navigation path based on context


  const helpItems = [
    {
      title: 'Frequently Asked Questions',
      description: 'Find answers to common questions about using the platform',
      icon: QuestionMarkCircleIcon,
      path: isCharityContext() ? '/charity/help/faq' : '/business/help/faq'
    },
    {
      title: 'Report an Issue',
      description: 'Experiencing a problem? Let us know so we can help',
      icon: ExclamationTriangleIcon,
      path: isCharityContext() ? '/charity/help/report-issue' : '/business/help/report-issue'
    },
    {
      title: 'Contact Support',
      description: 'Get in touch with our support team for assistance',
      icon: ChatBubbleLeftRightIcon,
      path: isCharityContext() ? '/charity/help/contact-support' : '/business/help/contact-support'
    },
    {
      title: 'Terms & Conditions',
      description: 'Review our terms of service',
      icon: DocumentTextIcon,
      path: '/terms-and-conditions'
    },
    {
      title: 'Privacy Policy',
      description: 'Learn how we handle your data',
      icon: ShieldCheckIcon,
      path: '/privacy-policy'
    },
    {
      title: 'About Us',
      description: 'Learn more about Catch of the Day',
      icon: InformationCircleIcon,
      path: isCharityContext() ? '/charity/about' : '/business/about'
    }
  ];

  return (
      <div className="max-w-5xl mx-auto">
        <div className="mb-6">
          <div className="flex items-center justify-between">
            <div>
              <h1 className="text-2xl font-bold" style={{ color: themeConfig.primary }}>
                Help Center
              </h1>
              <p className="text-gray-600 mt-2">
                Find answers, report issues, and get support for your Catch of the Day {isCharityContext() ? 'charity' : 'business'} account.
              </p>
            </div>
          </div>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {helpItems.map((item) => (
            <Link 
              key={item.title}
              to={item.path}
              className="block p-6 rounded-lg shadow-sm transition-all hover:shadow-md"
              style={{ 
                backgroundColor: themeConfig.cardBackground,
                borderLeft: `4px solid ${themeConfig.primary}`
              }}
            >
              <div className="flex items-start">
                <div className="flex-shrink-0">
                  <item.icon 
                    className="h-6 w-6" 
                    style={{ color: themeConfig.primary }} 
                  />
                </div>
                <div className="ml-4">
                  <h3 className="text-lg font-medium" style={{ color: themeConfig.text }}>
                    {item.title}
                  </h3>
                  <p className="mt-1 text-sm text-gray-500">
                    {item.description}
                  </p>
                </div>
              </div>
            </Link>
          ))}
        </div>
      </div>
  );
}

export default HelpCenter;
